import { useMutation } from "@apollo/client";
import { Button, Card, Checkbox, ControlGroup } from "@blueprintjs/core";
import _ from "lodash";
import { useState } from "react";
import {
  ArticleCategory,
  CreateKeyFieldInput,
  KeyField,
  UpdateKeyFieldInput,
} from "../../api/schema";
import { Table } from "../../components/Table";
import {
  DeleteModal,
  DeleteStateHelper,
} from "../../components/form/DeleteModal";
import { ViewHeader } from "../../components/form/Header";
import {
  UpdateStateHelper,
  ValuesVariables,
} from "../../components/form/InputForm";
import { UserLanguage } from "../../lang/lang";
import { CreateKeyFieldModal, UpdateKeyFieldModal } from "./Modals";
import {
  CreateKeyFieldMutation,
  CreateKeyFieldResponse,
  DeleteKeyFieldMutation,
  DeleteKeyFieldResponse,
  UpdateKeyFieldMutation,
  UpdateKeyFieldResponse,
} from "./graphql";

export const KeyFieldListView = (props: {
  keyFields: KeyField[];
  articleCategory: ArticleCategory;
  refetch: () => void;
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [updateModalState, setUpdateModalState] = useState<
    UpdateStateHelper<KeyField>
  >({ open: false, item: undefined });
  const [deleteModalState, setDeleteModalState] = useState<DeleteStateHelper>({
    open: false,
    id: undefined,
    prettyPrint: undefined,
  });

  const [createKeyField] = useMutation<
    CreateKeyFieldResponse,
    ValuesVariables<CreateKeyFieldInput>
  >(CreateKeyFieldMutation);

  const [updateKeyField] = useMutation<
    UpdateKeyFieldResponse,
    ValuesVariables<UpdateKeyFieldInput>
  >(UpdateKeyFieldMutation);

  const [deleteKeyField] = useMutation<DeleteKeyFieldResponse>(
    DeleteKeyFieldMutation
  );

  const refetch = () => {
    props.refetch();
  };

  // console.log(`key fields`, props.keyFields);

  return (
    <Card elevation={3}>
      <ViewHeader
        title={UserLanguage().pages.keyField.list}
        buttonText={UserLanguage().actions.create}
        open={setCreateModalOpen}
      />
      <CreateKeyFieldModal
        bundle={{
          open: createModalOpen,
          onClose: () => setCreateModalOpen(false),
          onSuccess: refetch,
          mutation: createKeyField,
          articleCategory: props.articleCategory,
        }}
      />
      <UpdateKeyFieldModal
        bundle={{
          modalState: updateModalState,
          mutation: updateKeyField,
          onClose: () => setUpdateModalState({ open: false, item: undefined }),
          onSuccess: () => {
            setUpdateModalState({ open: false, item: undefined });
            refetch();
          },
          articleCategory: props.articleCategory,
        }}
      />
      <DeleteModal<DeleteKeyFieldResponse>
        id={deleteModalState.id}
        open={deleteModalState.open}
        mutation={(id) => deleteKeyField({ variables: { id } })}
        texts={UserLanguage().pages.keyField.actions}
        resetOnClose
        onClose={() => {
          setDeleteModalState({ open: false, id: undefined });
        }}
        onSuccess={() => {
          setDeleteModalState({ open: false, id: undefined });
          refetch();
        }}
        type={UserLanguage().data.keyfield.label}
        prettyPrint={deleteModalState.prettyPrint}
      />
      <Table<KeyField>
        items={props.keyFields}
        columns={{
          id: { label: UserLanguage().data.id },
          name: {
            label: UserLanguage().data.name,
            render: (item) =>
              `${_.get(UserLanguage().data.fields, item.name, item.name)} (${
                item.name
              })`,
          },
          required: {
            label: UserLanguage().data.required,
            render: (item) => <Checkbox disabled checked={item.required} />,
          },
          actions: {
            label: UserLanguage().actions.label,
            render: (item) => (
              <ControlGroup>
                <Button
                  intent="primary"
                  rightIcon="edit"
                  onClick={() => {
                    console.log(`item`, item);
                    setUpdateModalState({
                      open: true,
                      item: item,
                    });
                  }}
                >
                  {UserLanguage().actions.edit}
                </Button>
                <Button
                  intent="danger"
                  rightIcon="delete"
                  onClick={() =>
                    setDeleteModalState({
                      open: true,
                      id: item.id,
                      prettyPrint: `${UserLanguage().data.name}: ${item.unit}`,
                    })
                  }
                >
                  {UserLanguage().actions.delete}
                </Button>
              </ControlGroup>
            ),
          },
        }}
      />
    </Card>
  );
};
